import { CheckCircle2, FilePen, Layers, Pencil, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Breadcrumb } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CountBadge } from '../../../../components/CountBadge';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { ErrorState } from '../../../../components/ErrorState';
import type { FolderTab } from '../../../../components/FolderTabs';
import type { MenuItem } from '../../../../components/Menu';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { usePermission } from '../../../../auth/AuthContext';
import { PreviewTable, type PreviewRow } from '../../../../components/PreviewTable';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell, ProfileHeaderSkeleton } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly, formatDateTime } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { SalaryGradeLineModal } from '../components/SalaryGradeLineModal';
import { SalaryGradeRateModal } from '../components/SalaryGradeRateModal';
import { SalaryGradeTrancheModal } from '../components/SalaryGradeTrancheModal';
import { salaryGradesApi } from '../plantillaApi';
import {
  SALARY_ACTIVATION,
  SALARY_TYPE,
  salaryActivationLabel,
  salaryTypeLabel,
  type SalaryGradeListItem,
  type SalaryTrancheDetail,
  type SalaryTypeValue,
} from '../plantillaTypes';
import { SALARY_GRADES, SALARY_STEPS } from '../utils/sampleSalaryGrades';

type TabKey = 'info' | 'grades';

const STEPS = Array.from({ length: SALARY_STEPS }, (_, step) => step);

/** One grade of the tranche, its rates by step — the shape the tranche form entered it in. */
interface GradeRow {
  grade: number;
  /** By position: index 0 is step 1. Null where the tranche has no rate at that step. */
  rates: (SalaryGradeListItem | null)[];
}

function toGradeRows(rates: SalaryGradeListItem[]): GradeRow[] {
  const byGrade = new Map<number, GradeRow>();

  for (const rate of rates) {
    const row = byGrade.get(rate.grade) ?? { grade: rate.grade, rates: STEPS.map(() => null) };
    row.rates[rate.step - 1] = rate;
    byGrade.set(rate.grade, row);
  }

  return [...byGrade.values()].sort((a, b) => a.grade - b.grade);
}

const isSalaryType = (value: number): value is SalaryTypeValue =>
  Object.values(SALARY_TYPE).includes(value as SalaryTypeValue);

/**
 * One tranche, on the **header** layout — its salary grades, as the grade × step table the
 * tranche form entered them in (product call, 16 Oct 2026: "inside the tranche are the declared
 * SGs").
 *
 * **It LANDS ON Salary grades**: a tranche is opened to read its schedule, not to check its
 * three fields, which the header already states.
 *
 * **A tranche is read off its rates, not stored**, so it is addressed by type and number and has
 * no fields of its own to edit. What it can do is take more grades, lose them all (Delete) and
 * get them back (Restore); each rate is corrected from its grade's row menu.
 */
export function SalaryTrancheProfilePage() {
  const params = useParams<{ type: string; tranche: string }>();

  const typeNumber = Number(params.type);
  const trancheNumber = Number(params.tranche);
  const type = isSalaryType(typeNumber) ? typeNumber : null;

  const [detail, setDetail] = useState<SalaryTrancheDetail | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [tab, setTab] = useState<TabKey>('grades');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<GradeRow | null>(null);
  const [confirming, setConfirming] = useState<ConfirmKind | null>(null);
  // The step cell clicked — one rate, corrected in its own form.
  const [editingRate, setEditingRate] = useState<{ row: GradeRow; step: number } | null>(null);
  const canUpdate = usePermission('salary-grades.update');
  const [running, setRunning] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const toast = useToast();
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  // A URL naming no salary type or no whole tranche is answered without asking the server.
  const addressable = type !== null && Number.isInteger(trancheNumber);

  useEffect(() => {
    if (type === null || !addressable) return;

    const controller = new AbortController();

    // Deleted rates included, so a deleted tranche still shows what it held.
    salaryGradesApi
      .getTranche(type, trancheNumber, true, controller.signal)
      .then((loaded) => {
        setDetail(loaded);
        setLoadError(null);
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setLoadError(caught instanceof ApiError ? caught : new ApiError('The tranche could not be loaded.', 0));
      });

    return () => controller.abort();
  }, [type, trancheNumber, addressable, reloadKey]);

  /*
    A LIVE tranche shows its live rates; a deleted one shows the newest of each grade and step
    it held, which is what a restore would put back.
  */
  const shown = useMemo(() => {
    if (!detail) return [];
    if (detail.isActive) return detail.rates.filter((r) => r.isActive);

    const newest = new Map<string, SalaryGradeListItem>();

    for (const rate of detail.rates) {
      const key = `${rate.grade}-${rate.step}`;
      const current = newest.get(key);
      if (!current || (rate.updatedAt ?? rate.createdAt) > (current.updatedAt ?? current.createdAt)) newest.set(key, rate);
    }

    return [...newest.values()];
  }, [detail]);

  const gradeRows = useMemo(() => toGradeRows(shown), [shown]);
  const heldGrades = useMemo(() => gradeRows.map((row) => row.grade), [gradeRows]);

  // Whole, so searched and paged in memory: a tranche holds at most 33 grades.
  const fetchPage = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim();
      const matched = term ? gradeRows.filter((row) => String(row.grade) === term) : gradeRows;
      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({ items: matched.slice(start, start + query.pageSize), total: matched.length });
    },
    [gradeRows],
  );

  const state = useDataTable<GradeRow>({ fetchPage, pageSize: 50 });

  async function runConfirmed() {
    if (!confirming || type === null) return;

    setRunning(true);
    setActionError(null);

    try {
      if (confirming === 'deactivate') await salaryGradesApi.deactivateTranche(type, trancheNumber);
      else if (confirming === 'restore') await salaryGradesApi.activateTranche(type, trancheNumber);
      else
        await salaryGradesApi.setTrancheActivation(
          type,
          trancheNumber,
          confirming === 'activate' ? SALARY_ACTIVATION.active : SALARY_ACTIVATION.draft,
        );

      toast.success(`Tranche ${trancheNumber} ${CONFIRM_COPY[confirming].done}.`);
      setConfirming(null);
      reload();
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  /*
    ONE Edit per LINE (product call, 16 Oct 2026: "the edit should be per line only") — the
    grade's every step in one form, replacing a menu of eight one-rate edits. A row-menu entry
    rather than the panel's `onEdit`, because a deleted tranche must say why it cannot be edited.
  */
  const rowActions = useCallback(
    (row: GradeRow): MenuItem[] => [
      {
        key: `edit-${row.grade}`,
        label: 'Edit',
        icon: <Pencil className="h-4 w-4" aria-hidden="true" />,
        permission: 'salary-grades.update',
        disabledReason: detail?.isActive ? null : 'This tranche is deleted. Restore it first.',
        onClick: () => setEditing(row),
      },
    ],
    [detail?.isActive],
  );

  /*
    EVERY STEP CELL OPENS ITS RATE (product call, 19 Oct 2026) — a click corrects that one
    amount. A plain value where it cannot: without the update key, on a deleted tranche, or on an
    empty step (adding a rate is Add grades). The row's Edit still corrects the whole line.
  */
  const cellsEditable = canUpdate && Boolean(detail?.isActive);

  const columns = useMemo<DataColumn<GradeRow>[]>(
    () => [
      {
        key: 'grade',
        header: 'Grade',
        width: 80,
        render: (row) => <TextCell value={`SG ${row.grade}`} />,
      },
      ...STEPS.map(
        (step): DataColumn<GradeRow> => ({
          key: `step${step + 1}`,
          header: `Step ${step + 1}`,
          width: 112,
          align: 'right',
          render: (row) => {
            const rate = row.rates[step];

            if (!rate || !cellsEditable) {
              return <TextCell value={rate ? formatMoney(rate.monthlyAmount) : null} mono />;
            }

            // A table cell's value tone and weight, told apart by its hover — never the dark
            // clickable treatment, which the root rule keeps off a table cell.
            return (
              <button
                type="button"
                id={`salary-tranche-grades-rate-${row.grade}-${step + 1}`}
                aria-label={`Edit SG ${row.grade} step ${step + 1}, ${formatMoney(rate.monthlyAmount)}`}
                onClick={() => setEditingRate({ row, step })}
                className="block w-full rounded text-right font-mono text-sm tabular-nums text-slate-500 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
              >
                {formatMoney(rate.monthlyAmount)}
              </button>
            );
          },
        }),
      ),
    ],
    [cellsEditable],
  );

  const tabs: FolderTab<TabKey>[] = [
    { key: 'info', label: 'Info' },
    {
      key: 'grades',
      label: (
        <>
          Salary grades
          <CountBadge count={detail?.gradeCount ?? 0} tone="slate" />
        </>
      ),
    },
  ];

  const title = `Tranche ${trancheNumber}`;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-white">
      <Breadcrumb
        segments={[
          'Human Resources',
          { label: 'Organization', to: '/organization/chart' },
          { label: 'Settings', to: '/organization/settings' },
          { label: 'Salary Grades', to: '/organization/settings/salary-grades' },
          detail ? `${title} · ${salaryTypeLabel(detail.type)}` : '',
        ]}
      />

      {!addressable ? (
        <ErrorState message="That tranche does not exist." />
      ) : loadError && !detail ? (
        <ErrorState
          message={loadError.message}
          correlationId={loadError.correlationId}
          onRetry={loadError.isNotFound ? undefined : reload}
        />
      ) : !detail ? (
        <ProfileHeaderSkeleton tabs={tabs.length} />
      ) : (
        <ProfileHeaderShell<TabKey>
          id="salary-tranche-profile"
          tabs={tabs}
          activeTab={tab}
          onTabChange={setTab}
          panel={tab === 'grades' ? 'bleed' : 'cards'}
          header={
            <ProfileHeader
              id="salary-tranche-profile-header"
              icon={<Layers className="h-5 w-5" />}
              title={title}
              meta={[
                <span key="type" className="font-sans">
                  {salaryTypeLabel(detail.type)}
                </span>,
                `Effective ${formatDateOnly(detail.effectivityDate)}`,
                <span key="activation" className="font-sans">
                  {salaryActivationLabel(detail.activationStatus)}
                </span>,
              ]}
              status={
                detail.isActive ? (
                  <StatusText tone="active">Active</StatusText>
                ) : (
                  <StatusText tone="inactive">Deleted</StatusText>
                )
              }
              createdBy={detail.createdByName}
              createdAt={detail.createdAt}
              settings={{
                onDelete: detail.isActive ? () => setConfirming('deactivate') : undefined,
                deletePermission: 'salary-grades.delete',
              }}
            />
          }
          banner={
            /*
              THE BANNER OWNS THE LIFECYCLE — Activate / Set to draft while live, Restore once
              deleted. One state, one sentence, one action, each behind its confirmation.
            */
            <ProfileBanner
              id="salary-tranche-profile-banner"
              tone={!detail.isActive ? 'slate' : detail.activationStatus === SALARY_ACTIVATION.active ? 'emerald' : 'amber'}
              message={
                !detail.isActive
                  ? 'This tranche is deleted. Its rates are not offered when a position is given a grade; restoring puts them back.'
                  : detail.activationStatus === SALARY_ACTIVATION.active
                    ? `Active — ${detail.gradeCount} grade${detail.gradeCount === 1 ? '' : 's'} and ${detail.rateCount} rate${detail.rateCount === 1 ? '' : 's'}, effective ${formatDateOnly(detail.effectivityDate)}. These are the rates a plantilla position's grade is read against.`
                    : `Draft — ${detail.gradeCount} grade${detail.gradeCount === 1 ? '' : 's'} and ${detail.rateCount} rate${detail.rateCount === 1 ? '' : 's'}, read by nothing outside Salary Grades until the tranche is activated.`
              }
              action={
                !detail.isActive ? (
                  <PermissionGate permission="salary-grades.delete">
                    <Button
                      id="salary-tranche-profile-restore"
                      variant="secondary"
                      leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                      onClick={() => setConfirming('restore')}
                    >
                      Restore
                    </Button>
                  </PermissionGate>
                ) : (
                  <PermissionGate permission="salary-grades.update">
                    {detail.activationStatus === SALARY_ACTIVATION.active ? (
                      <Button
                        id="salary-tranche-profile-draft"
                        variant="secondary"
                        leading={<FilePen className="h-4 w-4" aria-hidden="true" />}
                        onClick={() => setConfirming('draft')}
                      >
                        Set to draft
                      </Button>
                    ) : (
                      <Button
                        id="salary-tranche-profile-activate"
                        variant="secondary"
                        leading={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
                        onClick={() => setConfirming('activate')}
                      >
                        Activate
                      </Button>
                    )}
                  </PermissionGate>
                )
              }
            />
          }
        >
          {tab === 'info' ? (
            // Type, tranche and date are the header's; the counts are the banner's.
            <ProfileCard id="salary-tranche-profile-audit" title="Audit">
              <PreviewTable variant="table-display" rows={auditRows(detail)} />
            </ProfileCard>
          ) : (
            <div className="flex h-full min-w-0 flex-col p-4">
              <DataTablePanel<GradeRow>
                id="salary-tranche-grades"
                state={state}
                columns={columns}
                rowKey={(row) => String(row.grade)}
                fill
                selectable={false}
                searchPlaceholder="Search grade"
                emptyMessage="This tranche holds no grades."
                emptySearchMessage="No grade matches that number."
                onCreate={() => setAdding(true)}
                createLabel="Add grades"
                createPermission="salary-grades.create"
                createDisabledReason={
                  !detail.isActive
                    ? 'This tranche is deleted. Restore it before adding grades.'
                    : heldGrades.length >= SALARY_GRADES
                      ? `Every grade from 1 to ${SALARY_GRADES} is already in this tranche.`
                      : null
                }
                rowActions={rowActions}
              />
            </div>
          )}
        </ProfileHeaderShell>
      )}

      {adding && detail ? (
        <SalaryGradeTrancheModal
          open
          into={{
            type: detail.type,
            tranche: detail.tranche,
            effectivityDate: detail.effectivityDate,
            activationStatus: detail.activationStatus,
            heldGrades,
          }}
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            reload();
          }}
        />
      ) : null}

      {editing && detail ? (
        <SalaryGradeLineModal
          open
          type={detail.type}
          tranche={detail.tranche}
          grade={editing.grade}
          rates={editing.rates}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}

      {editingRate && detail ? (
        <SalaryGradeRateModal
          open
          type={detail.type}
          tranche={detail.tranche}
          grade={editingRate.row.grade}
          step={editingRate.step}
          rates={editingRate.row.rates}
          onClose={() => setEditingRate(null)}
          onSaved={() => {
            setEditingRate(null);
            reload();
          }}
        />
      ) : null}

      {confirming && detail ? (
        <ConfirmDialog
          open
          requireConfirm
          onClose={() => {
            setConfirming(null);
            setActionError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={CONFIRM_COPY[confirming].title}
          danger={CONFIRM_COPY[confirming].danger}
          loading={running}
          id={`salary-tranche-profile-${confirming}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>{CONFIRM_COPY[confirming].body(`${title} (${salaryTypeLabel(detail.type)})`, detail.rateCount)}</p>
              {actionError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {actionError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </div>
  );
}

/** The trail — who declared the tranche, and its latest correction. */
function auditRows(detail: SalaryTrancheDetail): PreviewRow[] {
  return [
    { label: 'Created by', value: detail.createdByName },
    { label: 'Created', value: formatDateTime(detail.createdAt), mono: true },
    { label: 'Updated by', value: detail.updatedByName },
    { label: 'Updated', value: detail.updatedAt != null ? formatDateTime(detail.updatedAt) : null, mono: true },
  ];
}

type ConfirmKind = 'deactivate' | 'restore' | 'activate' | 'draft';

/** Each confirmable act — its title, what the toast says it did, and the consequence before the tick. */
const CONFIRM_COPY: Record<ConfirmKind, { title: string; done: string; danger: boolean; body: (name: string, rates: number) => string }> = {
  deactivate: {
    title: 'Delete tranche',
    done: 'deleted',
    danger: true,
    body: (name, rates) =>
      `${name} and its ${rates} rates will stop being offered when a position is given a grade. Positions already carrying a grade keep it — a position records the grade, not the rate.`,
  },
  restore: {
    title: 'Restore tranche',
    done: 'restored',
    danger: false,
    body: (name) => `${name} and its rates will be offered again.`,
  },
  activate: {
    title: 'Activate tranche',
    done: 'activated',
    danger: false,
    body: (name, rates) =>
      `${name}'s ${rates} rates will be put in force: offered when a position is given a grade, and read by every post, vacancy and posting at that grade.`,
  },
  draft: {
    title: 'Set tranche to draft',
    done: 'set to draft',
    danger: false,
    body: (name) =>
      `${name} will stop being read outside Salary Grades — no grade picker offers its rates, and posts, vacancies and postings read the latest active rate for their grade instead, or none if there is none.`,
  },
};
